import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { profileAPI, cardAPI, uploadAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  User,
  Link2,
  Plus,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Image as ImageIcon,
  Phone,
  Mail,
  MapPin,
  Globe,
  MessageCircle,
  Instagram,
  Twitter,
  Linkedin,
  Facebook,
  Youtube,
  Github,
  Music2,
  AtSign,
  Twitch,
  Send,
  Upload,
  Loader2,
  Palette,
  X,
  LocateFixed
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const THEME_PRESETS = [
  {
    name: 'Violet (Default)',
    theme: {
      primaryColor: '#7c3aed',
      backgroundColor: '#020617',
      textColor: '#f8fafc',
      accentColor: '#a855f7',
      fontFamily: 'Inter'
    }
  },
  {
    name: 'Ocean Blue',
    theme: {
      primaryColor: '#0ea5e9',
      backgroundColor: '#0c1222',
      textColor: '#f0f9ff',
      accentColor: '#38bdf8',
      fontFamily: 'Inter'
    }
  },
  {
    name: 'Sunset Orange',
    theme: {
      primaryColor: '#f97316',
      backgroundColor: '#0f0a06',
      textColor: '#fff7ed',
      accentColor: '#fb923c',
      fontFamily: 'Inter'
    }
  },
  {
    name: 'Forest Green',
    theme: {
      primaryColor: '#10b981',
      backgroundColor: '#021008',
      textColor: '#ecfdf5',
      accentColor: '#34d399',
      fontFamily: 'Inter'
    }
  },
  {
    name: 'Royal Purple',
    theme: {
      primaryColor: '#9333ea',
      backgroundColor: '#0d0416',
      textColor: '#faf5ff',
      accentColor: '#a855f7',
      fontFamily: 'Inter'
    }
  },
  {
    name: 'Crimson Red',
    theme: {
      primaryColor: '#dc2626',
      backgroundColor: '#0f0404',
      textColor: '#fef2f2',
      accentColor: '#ef4444',
      fontFamily: 'Inter'
    }
  }
];

const FONT_OPTIONS = [
  { name: 'Inter', value: 'Inter' },
  { name: 'Roboto', value: 'Roboto' },
  { name: 'Open Sans', value: 'Open Sans' },
  { name: 'Lato', value: 'Lato' },
  { name: 'Montserrat', value: 'Montserrat' },
  { name: 'Poppins', value: 'Poppins' },
  { name: 'Playfair Display', value: 'Playfair Display' },
  { name: 'Merriweather', value: 'Merriweather' }
];

const SOCIAL_PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/username' },
  { id: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/username' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/username' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/username' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@username' },
  { id: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/username' },
  { id: 'tiktok', label: 'TikTok', icon: Music2, placeholder: 'https://tiktok.com/@username' },
  { id: 'threads', label: 'Threads', icon: AtSign, placeholder: 'https://threads.net/@username' },
  { id: 'twitch', label: 'Twitch', icon: Twitch, placeholder: 'https://twitch.tv/username' },
  { id: 'telegram', label: 'Telegram', icon: Send, placeholder: 'https://t.me/username' },
  { id: 'other', label: 'Other Link', icon: Link2, placeholder: 'https://example.com' },
];

const getSocialIcon = (platform) => {
  const found = SOCIAL_PLATFORMS.find(p => p.id === platform);
  return found ? found.icon : Link2;
};

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [cards, setCards] = useState([]);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  
  const profilePicRef = useRef(null);
  const galleryInputRef = useRef(null);
  const coverPhotoRef = useRef(null);
  
  const [formData, setFormData] = useState({
    slug: '',
    displayName: '',
    profilePicture: '',
    coverPhoto: '',
    shortBio: '',
    longBio: '',
    socialLinks: [],
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    address: '',
    latitude: '',
    longitude: '',
    images: [],
    theme: {
      primaryColor: '#7c3aed',
      backgroundColor: '#020617',
      textColor: '#f8fafc',
      accentColor: '#a855f7',
      fontFamily: 'Inter'
    }
  });

  useEffect(() => {
    fetchProfile();
    fetchCards();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await profileAPI.get();
      if (response.data.profile) {
        setProfile(response.data.profile);
        setFormData({
          slug: response.data.profile.slug || '',
          displayName: response.data.profile.displayName || '',
          profilePicture: response.data.profile.profilePicture || '',
          coverPhoto: response.data.profile.coverPhoto || '',
          shortBio: response.data.profile.shortBio || '',
          longBio: response.data.profile.longBio || '',
          socialLinks: response.data.profile.socialLinks || [],
          phone: response.data.profile.phone || '',
          whatsapp: response.data.profile.whatsapp || '',
          email: response.data.profile.email || '',
          website: response.data.profile.website || '',
          address: response.data.profile.address || '',
          latitude: response.data.profile.latitude || '',
          longitude: response.data.profile.longitude || '',
          images: response.data.profile.images || [],
          theme: response.data.profile.theme || {
            primaryColor: '#7c3aed',
            backgroundColor: '#020617',
            textColor: '#f8fafc',
            accentColor: '#a855f7',
            fontFamily: 'Inter'
          }
        });
        setSlugAvailable(true);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCards = async () => {
    try {
      const response = await cardAPI.getAll();
      setCards(response.data || []);
    } catch (error) {
      console.error('Failed to fetch cards:', error);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingProfile(true);
    try {
      const response = await uploadAPI.upload(file);
      setFormData({ ...formData, profilePicture: response.data.url });
      toast.success('Profile picture uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingProfile(false);
    }
  };

  // Upload gallery image
  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingGallery(true);
    try {
      const response = await uploadAPI.upload(file);
      setFormData({ ...formData, images: [...formData.images, response.data.url] });
      toast.success('Image added to gallery!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingGallery(false);
    }
  };

  // Upload cover photo
  const handleCoverPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCover(true);
    try {
      const response = await uploadAPI.upload(file);
      setFormData(prev => ({ ...prev, coverPhoto: response.data.url }));
      toast.success('Cover photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload cover photo');
    } finally {
      setUploadingCover(false);
    }
  };

  // Auto-fetch current location
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(position.coords.latitude.toFixed(6)),
          longitude: parseFloat(position.coords.longitude.toFixed(6))
        }));
        toast.success('Location fetched successfully!');
        setFetchingLocation(false);
      },
      (error) => {
        toast.error('Unable to get location. Please allow location access.');
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const checkSlug = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setCheckingSlug(true);
    try {
      const response = await profileAPI.checkSlug(slug);
      setSlugAvailable(response.data.available);
    } catch (error) {
      setSlugAvailable(null);
    } finally {
      setCheckingSlug(false);
    }
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData({ ...formData, slug: value });
    checkSlug(value);
  };

  const addSocialLink = () => {
    setFormData({
      ...formData,
      socialLinks: [...formData.socialLinks, { platform: 'instagram', url: '', label: '' }]
    });
  };

  const updateSocialLink = (index, field, value) => {
    const links = [...formData.socialLinks];
    links[index][field] = value;
    setFormData({ ...formData, socialLinks: links });
  };

  const removeSocialLink = (index) => {
    const links = formData.socialLinks.filter((_, i) => i !== index);
    setFormData({ ...formData, socialLinks: links });
  };

  const addImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setFormData({ ...formData, images: [...formData.images, url] });
    }
  };

  const removeImage = (index) => {
    const images = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.slug || formData.slug.length < 3) {
      toast.error('Slug must be at least 3 characters');
      return;
    }

    if (!slugAvailable && !profile) {
      toast.error('This slug is not available');
      return;
    }

    setSaving(true);
    try {
      const response = await profileAPI.create(formData);
      setProfile(response.data.profile);
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const copyProfileUrl = () => {
    const url = `${window.location.origin}/p/${formData.slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Profile URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const linkToCard = async (cardUrlCode) => {
    const profileUrl = `${window.location.origin}/p/${formData.slug}`;
    try {
      await cardAPI.updateUrl({ url_code: cardUrlCode, url: profileUrl });
      toast.success('Card linked to your profile!');
      fetchCards();
    } catch (error) {
      toast.error('Failed to link card');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="profile-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">My Profile</h1>
        <p className="text-slate-400">Build your digital identity card profile</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Slug Section */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Link2 className="w-5 h-5 text-violet-400" />
                Profile URL
              </h2>
              
              <div className="flex items-center gap-2 mb-2">
                <span className="text-slate-400 text-sm">{window.location.origin}/p/</span>
                <Input
                  value={formData.slug}
                  onChange={handleSlugChange}
                  className="input-field flex-1"
                  placeholder="your-slug"
                  required
                  minLength={3}
                  data-testid="profile-slug"
                />
                {formData.slug && (
                  <span className={`text-sm ${slugAvailable ? 'text-emerald-400' : slugAvailable === false ? 'text-red-400' : 'text-slate-400'}`}>
                    {checkingSlug ? '...' : slugAvailable ? '✓ Available' : slugAvailable === false ? '✗ Taken' : ''}
                  </span>
                )}
              </div>

              {profile && formData.slug && (
                <div className="flex gap-2 mt-4">
                  <Button
                    type="button"
                    onClick={copyProfileUrl}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    Copy URL
                  </Button>
                  <Button
                    type="button"
                    onClick={() => window.open(`/p/${formData.slug}`, '_blank')}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Preview
                  </Button>
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-violet-400" />
                Basic Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Display Name</label>
                  <Input
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="input-field"
                    placeholder="John Doe"
                    data-testid="profile-name"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Profile Picture</label>
                  <div className="flex items-center gap-4">
                    {formData.profilePicture ? (
                      <img
                        src={getImageUrl(formData.profilePicture)}
                        alt="Profile"
                        className="w-20 h-20 rounded-full object-cover border-2 border-violet-500"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center">
                        <User className="w-8 h-8 text-slate-600" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={profilePicRef}
                        onChange={handleProfilePictureUpload}
                        accept="image/*"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        onClick={() => profilePicRef.current?.click()}
                        className="btn-secondary text-sm flex items-center gap-2"
                        disabled={uploadingProfile}
                        data-testid="upload-profile-pic-btn"
                      >
                        {uploadingProfile ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="w-4 h-4" /> Upload Photo</>
                        )}
                      </Button>
                      {formData.profilePicture && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, profilePicture: '' })}
                          className="text-red-400 text-sm mt-2 hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Cover Photo (Banner)</label>
                  <div className="space-y-3">
                    {formData.coverPhoto ? (
                      <div className="relative">
                        <img
                          src={getImageUrl(formData.coverPhoto)}
                          alt="Cover"
                          className="w-full h-32 object-cover rounded-xl border border-slate-700"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, coverPhoto: '' }))}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                          data-testid="remove-cover-photo-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-full h-32 rounded-xl bg-slate-800 border-2 border-dashed border-slate-700 flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-slate-600" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={coverPhotoRef}
                      onChange={handleCoverPhotoUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      onClick={() => coverPhotoRef.current?.click()}
                      className="btn-secondary text-sm flex items-center gap-2"
                      disabled={uploadingCover}
                      data-testid="upload-cover-photo-btn"
                    >
                      {uploadingCover ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="w-4 h-4" /> {formData.coverPhoto ? 'Change Cover Photo' : 'Upload Cover Photo'}</>
                      )}
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Short Bio (tagline)</label>                  <Input
                    value={formData.shortBio}
                    onChange={(e) => setFormData({ ...formData, shortBio: e.target.value })}
                    className="input-field"
                    placeholder="Digital Marketing Expert | Founder"
                    maxLength={100}
                    data-testid="profile-short-bio"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Long Bio</label>
                  <textarea
                    value={formData.longBio}
                    onChange={(e) => setFormData({ ...formData, longBio: e.target.value })}
                    className="input-field resize-none w-full"
                    placeholder="Tell people more about yourself..."
                    rows={4}
                    data-testid="profile-long-bio"
                  />
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-violet-400" />
                  Social Links
                </h2>
                <Button
                  type="button"
                  onClick={addSocialLink}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Link
                </Button>
              </div>

              <div className="space-y-4">
                {formData.socialLinks.map((link, index) => {
                  const Icon = getSocialIcon(link.platform);
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-xl"
                    >
                      <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-violet-400" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <select
                          value={link.platform}
                          onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                          className="input-field text-sm"
                        >
                          {SOCIAL_PLATFORMS.map(p => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                          ))}
                        </select>
                        <Input
                          value={link.url}
                          onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                          className="input-field text-sm"
                          placeholder={SOCIAL_PLATFORMS.find(p => p.id === link.platform)?.placeholder}
                        />
                        <Input
                          value={link.label || ''}
                          onChange={(e) => updateSocialLink(index, 'label', e.target.value)}
                          className="input-field text-sm"
                          placeholder="Custom label (optional)"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSocialLink(index)}
                        className="text-slate-400 hover:text-red-400 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  );
                })}

                {formData.socialLinks.length === 0 && (
                  <p className="text-slate-500 text-center py-4">No social links added yet</p>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5 text-violet-400" />
                Contact Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field"
                    placeholder="+91 98765 43210"
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">WhatsApp</label>
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="input-field"
                    placeholder="+91 98765 43210"
                    data-testid="profile-whatsapp"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-field"
                    placeholder="john@example.com"
                    data-testid="profile-email"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Website</label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="input-field"
                    placeholder="https://yourwebsite.com"
                    data-testid="profile-website"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-slate-400 mb-2">Address</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="input-field"
                    placeholder="123 Main Street, City, Country"
                    data-testid="profile-address"
                  />
                </div>

                {/* Location Coordinates */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm text-slate-400">Location Coordinates <span className="text-slate-600 text-xs">(for precise map pin)</span></label>
                    <Button
                      type="button"
                      onClick={handleGetLocation}
                      className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5 h-auto"
                      disabled={fetchingLocation}
                      data-testid="get-location-btn"
                    >
                      {fetchingLocation ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching...</>
                      ) : (
                        <><LocateFixed className="w-3.5 h-3.5" /> Autofetch Location</>
                      )}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Latitude</label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        className="input-field text-sm"
                        placeholder="28.6139"
                        data-testid="profile-latitude"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Longitude</label>
                      <Input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        className="input-field text-sm"
                        placeholder="77.2090"
                        data-testid="profile-longitude"
                      />
                    </div>
                  </div>
                  {formData.latitude && formData.longitude && (
                    <a
                      href={`https://maps.google.com?q=${formData.latitude},${formData.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300"
                      data-testid="preview-map-link"
                    >
                      <MapPin className="w-3.5 h-3.5" />
                      Preview on Google Maps ({parseFloat(formData.latitude).toFixed(6)}°, {parseFloat(formData.longitude).toFixed(6)}°)
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-violet-400" />
                  Gallery Images
                </h2>
                <Button
                  type="button"
                  onClick={addImage}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Image
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-24 object-cover rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>

              {formData.images.length === 0 && (
                <p className="text-slate-500 text-center py-4">No gallery images added yet</p>
              )}
            </div>

            {/* Theme Customization */}
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-violet-400" />
                Theme Customization
              </h2>

              {/* Preset Themes */}
              <div className="mb-6">
                <label className="block text-sm text-slate-400 mb-3">Quick Themes</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {THEME_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setFormData({ ...formData, theme: preset.theme })}
                      className="p-3 rounded-xl border-2 transition-all hover:scale-105"
                      style={{
                        backgroundColor: preset.theme.backgroundColor,
                        borderColor: preset.theme.primaryColor,
                        opacity: 0.9
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: preset.theme.primaryColor }}
                        />
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: preset.theme.accentColor }}
                        />
                      </div>
                      <p className="text-xs font-medium" style={{ color: preset.theme.textColor }}>
                        {preset.name}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Primary Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.theme.primaryColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, primaryColor: e.target.value }
                      })}
                      className="w-12 h-12 rounded-lg cursor-pointer"
                    />
                    <Input
                      value={formData.theme.primaryColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, primaryColor: e.target.value }
                      })}
                      className="input-field flex-1"
                      placeholder="#7c3aed"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Background Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.theme.backgroundColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, backgroundColor: e.target.value }
                      })}
                      className="w-12 h-12 rounded-lg cursor-pointer"
                    />
                    <Input
                      value={formData.theme.backgroundColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, backgroundColor: e.target.value }
                      })}
                      className="input-field flex-1"
                      placeholder="#020617"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Text Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.theme.textColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, textColor: e.target.value }
                      })}
                      className="w-12 h-12 rounded-lg cursor-pointer"
                    />
                    <Input
                      value={formData.theme.textColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, textColor: e.target.value }
                      })}
                      className="input-field flex-1"
                      placeholder="#f8fafc"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Accent Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.theme.accentColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, accentColor: e.target.value }
                      })}
                      className="w-12 h-12 rounded-lg cursor-pointer"
                    />
                    <Input
                      value={formData.theme.accentColor}
                      onChange={(e) => setFormData({
                        ...formData,
                        theme: { ...formData.theme, accentColor: e.target.value }
                      })}
                      className="input-field flex-1"
                      placeholder="#a855f7"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">Font Family</label>
                  <select
                    value={formData.theme.fontFamily}
                    onChange={(e) => setFormData({
                      ...formData,
                      theme: { ...formData.theme, fontFamily: e.target.value }
                    })}
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-violet-500 focus:outline-none"
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Theme Preview */}
              <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: formData.theme.backgroundColor }}>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-3"
                    style={{
                      background: `linear-gradient(135deg, ${formData.theme.primaryColor}, ${formData.theme.accentColor})`
                    }}
                  />
                  <p className="font-semibold mb-1" style={{ color: formData.theme.textColor, fontFamily: formData.theme.fontFamily }}>
                    Preview Text
                  </p>
                  <p className="text-sm opacity-70" style={{ color: formData.theme.textColor, fontFamily: formData.theme.fontFamily }}>
                    This is how your profile will look
                  </p>
                  <div className="flex justify-center gap-2 mt-3">
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: formData.theme.primaryColor }}
                    />
                    <div
                      className="w-8 h-8 rounded-full"
                      style={{ backgroundColor: formData.theme.accentColor }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full btn-primary text-lg py-4"
              disabled={saving || (!slugAvailable && !profile)}
              data-testid="profile-save-btn"
            >
              {saving ? 'Saving...' : profile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </form>
        </div>

        {/* Sidebar - Link to Cards */}
        <div className="space-y-6">
          {/* Link to Card Section */}
          {profile && cards.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Link to MeTag Card</h2>
              <p className="text-slate-400 text-sm mb-4">
                Connect your profile to your MeTag NFC card for instant sharing.
              </p>
              
              <div className="space-y-3">
                {cards.map((card) => (
                  <div key={card.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                    <div>
                      <p className="text-white text-sm font-medium">Card #{card.urlCode?.slice(0, 8)}</p>
                      <p className="text-slate-500 text-xs">
                        {card.isLinked ? 'Linked' : 'Not linked'}
                      </p>
                    </div>
                    <Button
                      onClick={() => linkToCard(card.urlCode)}
                      className="btn-primary text-xs px-3 py-1"
                      disabled={card.linkedUrl === `${window.location.origin}/p/${formData.slug}`}
                    >
                      {card.linkedUrl === `${window.location.origin}/p/${formData.slug}` ? 'Linked' : 'Link'}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Card */}
          {formData.slug && (
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Preview</h2>
              <div className="text-center">
                {formData.profilePicture ? (
                  <img
                    src={formData.profilePicture}
                    alt="Profile"
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-2 border-violet-500"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-violet-500/20 flex items-center justify-center">
                    <User className="w-10 h-10 text-violet-400" />
                  </div>
                )}
                <h3 className="text-xl font-bold text-white">{formData.displayName || 'Your Name'}</h3>
                <p className="text-slate-400 text-sm mt-1">{formData.shortBio || 'Your tagline'}</p>
                
                {formData.socialLinks.length > 0 && (
                  <div className="flex justify-center gap-2 mt-4">
                    {formData.socialLinks.slice(0, 5).map((link, i) => {
                      const Icon = getSocialIcon(link.platform);
                      return (
                        <div key={i} className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-slate-400" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stats */}
          {profile && (
            <div className="glass rounded-2xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Stats</h2>
              <div className="text-center">
                <p className="text-4xl font-bold text-violet-400">{profile.views || 0}</p>
                <p className="text-slate-400 text-sm">Profile Views</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
